import { useEffect, useState } from 'react';

import { getExtensionsForFileType, getExtensionGroup, getMaxBytesForExtension } from '../../../constants';
import { UploadDestination } from '../../../../../enums/uploadDestination';
import { FileNode } from '../../../../../types/FileManagerTypes';
import { DraggerModalStep } from '../DraggerModalStep';

interface ReuploadModalProps {
  show: boolean;
  hide: () => void;
  selectedFiles: FileList | File[] | null;
  /** The existing dataset being replaced. */
  reuploadFiles?: FileNode[];
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>, isDragged?: boolean) => Promise<void>;
  handleFileUpload: (reuploadList?: FileNode[], onSuccess?: () => void, destinationType?: UploadDestination) => Promise<void>;
  isUploading: boolean;
}

export const ReuploadModal = ({ show, hide, selectedFiles, reuploadFiles, handleFileChange, handleFileUpload, isUploading }: ReuploadModalProps) => {
  const [requiredExtensions, setRequiredExtensions] = useState<string[]>([]);
  const [isMultiPart, setIsMultiPart] = useState<boolean>(false);
  const [disableDragger, setDisableDragger] = useState<boolean>(true);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<string>('');

  const maxBytesForFile = (file: File) => getMaxBytesForExtension(file.name);

  const clearFiles = () => {
    const event = {
      target: { files: new DataTransfer().files },
    } as React.ChangeEvent<HTMLInputElement>;
    handleFileChange(event);
  };

  const setDefault = () => {
    setDisableDragger(true);
    setRequiredExtensions([]);
    setCanSubmit(false);
    setErrorMessages('');
    clearFiles();
  };

  const handleClose = () => {
    setDefault();
    hide();
  };

  // Derive what the replacement set must look like from the existing dataset.
  // Extensions are stored without the leading dot to match FileDragger's
  // per-file extension check.
  useEffect(() => {
    if (!reuploadFiles || reuploadFiles.length === 0) return;
    setErrorMessages('');

    const extensions = new Set<string>();
    for (const file of reuploadFiles) {
      const name: string = file.name || '';
      extensions.add(name.slice(name.lastIndexOf('.') + 1).toLowerCase());
    }

    const shapefileParts = getExtensionsForFileType('SHAPEFILE');
    const isShapefile = [...extensions].some((ext) => shapefileParts.includes(ext));

    if (isShapefile) {
      // The existing dataset usually surfaces as a single .shp node; reupload
      // requires the full component set (.shp/.shx/.dbf/.prj), the parts sharing
      // one prefix — which need not match the original dataset's name.
      setRequiredExtensions(shapefileParts);
      setIsMultiPart(true);
      setDisableDragger(false);
    } else if (extensions.size === 1) {
      // Accept any extension equivalent to the original (jpg/jpeg, doc/docx,
      // xls/xlsx), matching what the upload flow allows for that file type.
      const [ext] = [...extensions];
      setRequiredExtensions(getExtensionGroup(ext));
      setIsMultiPart(false);
      setDisableDragger(false);
    } else {
      setErrorMessages('You can only reupload one file at a time');
      setDisableDragger(true);
    }
  }, [reuploadFiles]);

  const handleUpload = async () => {
    try {
      // Close (and reset) only on real success — a failure keeps this modal
      // open with selectedFiles intact so the user can retry immediately.
      await handleFileUpload(
        reuploadFiles,
        () => {
          setDefault();
          hide();
        },
        UploadDestination.REUPLOAD,
      );
    } catch (error) {
      console.error('Error reuploading files:', error);
    }
  };

  return (
    <DraggerModalStep
      open={show}
      onClose={handleClose}
      headerTitle="Reupload"
      requiredExtensions={requiredExtensions}
      isMultiPart={isMultiPart}
      accept={requiredExtensions.map((ext: string) => `.${ext}`).join(',')}
      placeholderLabel={(requiredExtensions[0] ?? '').toUpperCase()}
      selectedFiles={selectedFiles}
      handleFileChange={handleFileChange}
      onValidityChange={setCanSubmit}
      draggerDisabled={disableDragger}
      maxBytesFor={maxBytesForFile}
      errorMessage={errorMessages}
      onBack={handleClose}
      backLabel="Back"
      onSubmit={handleUpload}
      submitLabel="Upload"
      submitDisabled={!canSubmit || isUploading}
    />
  );
};
